/**
 * Messages list + send mutation backed by `/v1/messages`.
 *
 * The list polls every 5 seconds so the operator sees status transitions
 * (queued → sent → delivered) without having to refresh. We pause polling
 * when the tab is hidden to avoid burning the API + the user's data.
 *
 * The send mutation requires an `Idempotency-Key` (Stripe-style); we generate
 * a fresh UUID per submit and let the API dedupe duplicates within the cache
 * window if the user double-clicks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface MessageListItem {
  id: number;
  uuid: string;
  to: string;
  from: string;
  body: string;
  segments: number;
  encoding: string;
  status: string;
  provider_id?: number | null;
  /**
   * Mirrors providers.supports_dlr (migration 000085). When false,
   * `status='sent'` is terminal — the carrier never sends delivery
   * receipts (e.g. Brevo). Used by `messageDisplayStatus` below so
   * the UI shows "Delivered" instead of "Sending..." for these rows.
   * Defaults to true on the server when the lookup fails or the
   * provider was deleted (safer to render `sent` as transient).
   */
  provider_supports_dlr: boolean;
  route_id?: number | null;
  provider_msg_id?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  cost: number;
  price: number;
  created_at: string;
  sent_at?: string | null;
  delivered_at?: string | null;
}

/**
 * Derives the status the UI should render from the row's raw status +
 * carrier's DLR capability.
 *
 * For providers without webhook delivery receipts (Brevo today —
 * `provider_supports_dlr === false`) the carrier never transitions a
 * row out of `sent`, so showing "Sending..." misleads operators into
 * thinking the message is still in flight. As far as we can tell it
 * was delivered, so we promote `sent` → `delivered` for these rows.
 *
 * Every other row passes through unchanged so the existing semantics
 * for Twilio / Bandwidth / smsgateway are untouched.
 */
export function messageDisplayStatus(
  status: string,
  providerSupportsDLR: boolean,
): string {
  if (status === 'sent' && providerSupportsDLR === false) {
    return 'delivered';
  }
  return status;
}

export interface MessageListResponse {
  items: MessageListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface SendMessageInput {
  to: string;
  from: string;
  body: string;
  message_type?: 'marketing' | 'transactional' | '2fa';
}

export interface SendMessageResponse {
  id: string;
  status: string;
  to: string;
  from: string;
  segments: number;
  estimated_cost: number;
  created_at: string;
}

const MESSAGES_KEY = ['messages', 'list'] as const;

interface ListParams {
  limit?: number;
  offset?: number;
  /** When false the list won't poll — useful when the tab is hidden. */
  enabled?: boolean;
}

/**
 * Polls GET /v1/messages every 5s. Pass `enabled: false` to pause without
 * losing cached data (we use this when the browser tab is hidden).
 */
export function useMessagesList({ limit = 50, offset = 0, enabled = true }: ListParams = {}) {
  return useQuery({
    queryKey: [...MESSAGES_KEY, { limit, offset }],
    queryFn: () =>
      api.get<MessageListResponse>(`/v1/messages?limit=${limit}&offset=${offset}`),
    enabled,
    refetchInterval: enabled ? 5_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

/** RFC4122-ish v4 UUID; good enough for an idempotency key. */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts (rare in modern browsers).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SendMessageInput) =>
      api.post<SendMessageResponse>('/v1/messages', input, {
        headers: { 'Idempotency-Key': generateUUID() },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MESSAGES_KEY });
    },
  });
}
